import { SERVICE_GROUPS, SERVICE_ICONS, waLink } from "../data/clinic";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, Eyebrow } from "./Reveal";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

function ServiceCard({ id, item, highlight = false }) {
  const Icon = SERVICE_ICONS[id];
  return (
    <motion.div
      whileHover={{ y: -6 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className={`group flex h-full flex-col rounded-3xl border p-6 md:p-7 ${
        highlight
          ? "border-transparent bg-brand-primary text-white shadow-xl shadow-brand-primary/20"
          : "border-brand-border bg-brand-surface"
      }`}
    >
      <div
        className={`mb-5 flex h-12 w-12 items-center justify-center rounded-2xl ${
          highlight ? "bg-white/15 text-white" : "bg-brand-primary/10 text-brand-primary"
        }`}
      >
        <Icon className="h-6 w-6" strokeWidth={1.5} />
      </div>
      <h3 className={`font-display text-xl leading-snug ${highlight ? "text-white" : "text-brand-ink"}`}>
        {item.name}
      </h3>
      <p className={`mt-2 text-sm leading-relaxed ${highlight ? "text-white/80" : "text-brand-inksoft"}`}>
        {item.desc}
      </p>
    </motion.div>
  );
}

export default function Services() {
  const { lang, t } = useLang();
  const s = t.services;
  const group = (key) => SERVICE_GROUPS.find((g) => g.key === key);

  return (
    <section id="services" data-testid="services-section" className="relative bg-brand-surface py-20 md:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Eyebrow>{s.eyebrow}</Eyebrow>
          <div className="mt-5 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div className="max-w-2xl">
              <h2 className="font-display text-4xl font-normal leading-tight tracking-tight text-brand-ink md:text-5xl">
                {s.heading}
              </h2>
              {s.subtitle && (
                <p className="mt-4 text-base leading-relaxed text-brand-inksoft md:text-lg">
                  {s.subtitle}
                </p>
              )}
            </div>
            <a
              href={waLink(null, lang)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="services-book-btn"
              className="inline-flex shrink-0 items-center gap-2 rounded-full border border-brand-primary px-6 py-3 text-sm font-semibold text-brand-primary transition-colors hover:bg-brand-primary hover:text-white"
            >
              {t.common.bookConsultation} <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </Reveal>

        {/* Signature */}
        <div className="mt-14">
          <Reveal>
            <p className="mb-6 text-sm font-semibold uppercase tracking-[0.2em] text-brand-warm">
              ✦ {s.groups.signature.label} — {s.groups.signature.tag}
            </p>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {group("signature").ids.map((id, i) => (
              <Reveal key={id} delay={i * 0.06} className="h-full">
                <ServiceCard id={id} item={s.items[id]} highlight />
              </Reveal>
            ))}
          </div>
        </div>

        {/* General & Cosmetic */}
        <div className="mt-16">
          <Reveal>
            <p className="mb-6 text-sm font-semibold uppercase tracking-[0.2em] text-brand-primary">
              {s.groups.general.label} — {s.groups.general.tag}
            </p>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {group("general").ids.map((id, i) => (
              <Reveal key={id} delay={(i % 3) * 0.06} className="h-full">
                <ServiceCard id={id} item={s.items[id]} />
              </Reveal>
            ))}
          </div>
        </div>

        {/* Gum & Oral Surgery */}
        <div className="mt-16 grid gap-5 lg:grid-cols-3">
          <Reveal className="lg:col-span-3">
            <p className="mb-1 text-sm font-semibold uppercase tracking-[0.2em] text-brand-primary">
              {s.groups.surgery.label} — {s.groups.surgery.tag}
            </p>
          </Reveal>
          {group("surgery").ids.map((id, i) => (
            <Reveal key={id} delay={i * 0.06} className="h-full">
              <ServiceCard id={id} item={s.items[id]} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
